<template>
  <div class="share-popup">
    <!-- 桌面端 Popover -->
    <BasePopover
      v-if="isDesktopPopupMode"
      :popup-visible="desktopPopupVisible"
      :trigger="desktopTrigger"
      position="bottom"
      content-class="share-popup__popover-container"
      :content-style="{ padding: '24px 32px' }"
      @popup-visible-change="handlePopoverVisibleChange"
    >
      <div class="share-popup__trigger">
        <slot name="trigger">
          <div class="share-popup__default-trigger">
            <IconShareAlt class="icon" />
            分享
          </div>
        </slot>
      </div>
      <template #content>
        <div class="share-popup__content">
          <div class="share-popup__title">扫码分享</div>
          <div ref="qrcodeRef" class="share-popup__qrcode">
            <img v-if="qrDataUrl" :src="qrDataUrl" alt="分享二维码" />
            <a-skeleton v-else :animation="true">
              <a-skeleton-shape shape="square" />
            </a-skeleton>
          </div>
          <div class="share-popup__footer">
            <a-button type="text" class="share-popup__copy-btn" @click="handleCopyLink">
              <template #icon><IconCopy /></template>
              复制链接
            </a-button>
          </div>
        </div>
      </template>
    </BasePopover>

    <!-- 移动端 Drawer -->
    <template v-else>
      <div ref="triggerRef" class="share-popup__trigger" @click="drawerVisible = true">
        <slot name="trigger">
          <div class="share-popup__default-trigger">
            <IconShareAlt class="icon" />
            分享
          </div>
        </slot>
      </div>

      <a-drawer
        v-model:visible="drawerVisible"
        placement="bottom"
        :footer="false"
        :header="false"
        :closable="false"
        :mask-closable="true"
        :esc-to-close="true"
        unmount-on-close
        :height="520"
        :drawer-style="{ borderRadius: '20px 20px 0 0' }"
        :body-style="{
          background: THEME_CSS_VARS.bgPage,
          padding: '10px 0',
        }"
        @cancel="drawerVisible = false"
      >
        <div class="share-popup__drawer-body">
          <div class="share-popup__drawer-close" @click="drawerVisible = false">
            <img :src="withBase('/images/icons/close.svg')" alt="关闭" />
          </div>
          <div class="share-popup__drawer-content">
            <div class="share-popup__drawer-title">扫码分享</div>
            <div ref="drawerQrcodeRef" class="share-popup__drawer-qrcode">
              <img v-if="qrDataUrl" :src="qrDataUrl" alt="分享二维码" />
              <a-skeleton v-else :animation="true">
                <a-skeleton-shape shape="square" />
              </a-skeleton>
            </div>
            <div class="share-popup__drawer-actions">
              <div class="share-popup__drawer-action" @click="handleCopyLink">
                <SvgIcon
                  name="link-share-circle"
                  color="#2DAFFF"
                  size="40"
                  class="share-popup__drawer-action-icon"
                />
                <span>复制链接</span>
              </div>
              <div class="share-popup__drawer-action" @click="handleSaveQrcode">
                <img :src="withBase('/images/icons/download.svg')" alt="保存" />
                <span>保存二维码</span>
              </div>
            </div>
          </div>
        </div>
      </a-drawer>
    </template>
  </div>
</template>

<script setup>
/**
 * @description 分享弹窗组件（桌面端 Popover + 移动端 Drawer）
 * @param {string} shareUrl - 要分享的链接，默认为当前页面 URL
 * @param {string} shareTitle - 分享标题；传入后复制内容为“标题，文件详情链接：地址”
 * @param {string} shareLinkLabel - 分享链接文案标签
 * @emits copy - 复制链接时触发
 * @emits save - 保存二维码时触发
 * @emits share - 分享成功时触发（复制链接或保存二维码）
 */

import { ref, computed, watch, onMounted } from 'vue'
import QRCode from 'qrcode'
import { IconShareAlt, IconCopy } from '@arco-design/web-vue/es/icon'
import { BaseMessage } from '~/utils/baseMessage'
import { useMediaQuery } from '@/composables/useMediaQuery'
import { getAbsoluteUrl } from '@/utils'
import { useCopyText } from '~/composables/useCopyText'
import BasePopover from '~/components/base/Popover.vue'
import useBasePath from '~/composables/useBasePath'
import { THEME_COLORS, THEME_CSS_VARS } from '~/utils/theme'

const props = defineProps({
  /**
   * @description 要分享的链接（可选，默认根据此链接动态生成二维码，若不传则取当前页面 URL）
   */
  shareUrl: {
    type: String,
    default: '',
  },
  /**
   * @description 分享标题，仅影响剪贴板内容；二维码始终编码纯链接。
   */
  shareTitle: {
    type: String,
    default: '',
  },
  shareLinkLabel: {
    type: String,
    default: '文件详情链接',
  },
  /**
   * @description 桌面端强制显示分享弹层，用于页面还原调试。
   */
  forceDesktopVisible: {
    type: Boolean,
    default: false,
  },
  /**
   * @description 桌面端弹层触发方式，支持按设计稿在不同入口切换 hover/click。
   */
  desktopTrigger: {
    type: String,
    default: 'hover',
    validator: (value) => ['hover', 'click', 'focus', 'contextMenu'].includes(value),
  },
})

const { withBase } = useBasePath()
const { copyText } = useCopyText()
const emit = defineEmits(['copy', 'save', 'share'])

const { isMobile } = useMediaQuery()

const popoverVisible = ref(false)
const drawerVisible = ref(false)
const triggerRef = ref(null)
const qrcodeRef = ref(null)
const drawerQrcodeRef = ref(null)

const qrDataUrl = ref('')

const desktopPopupVisible = computed(() => {
  if (props.forceDesktopVisible) return true
  if (props.desktopTrigger === 'hover') return null
  return popoverVisible.value
})
const isDesktopPopupMode = computed(() => props.forceDesktopVisible || !isMobile.value)

/**
 * @description 获取要分享的链接
 */
const currentShareUrl = computed(() => {
  return getAbsoluteUrl(props.shareUrl)
})

/**
 * @description 获取剪贴板分享文案；二维码仍单独使用纯分享地址。
 */
const currentCopyText = computed(() => {
  const title = props.shareTitle.trim()
  return title
    ? `${title}，${props.shareLinkLabel}：${currentShareUrl.value}`
    : currentShareUrl.value
})

/**
 * @description 根据分享地址生成二维码
 */
const generateQRCode = async () => {
  if (!import.meta.client || !currentShareUrl.value) return

  try {
    qrDataUrl.value = await QRCode.toDataURL(currentShareUrl.value, {
      width: 300,
      margin: 1,
      errorCorrectionLevel: 'H',
      color: {
        dark: '#000000',
        light: THEME_COLORS.bgSurface,
      },
    })
  } catch (err) {
    console.error('Failed to copy share link:', err)
    console.error('Failed to generate QR code:', err)
    qrDataUrl.value = ''
  }
}

// 监听内容变化重新生成
watch(
  () => currentShareUrl.value,
  () => {
    generateQRCode()
  }
)

onMounted(() => {
  generateQRCode()
})

/**
 * @description 复制链接到剪贴板
 */
const handleCopyLink = async () => {
  if (!import.meta.client) return

  try {
    await copyText(currentCopyText.value)
    BaseMessage.success('已复制链接')
    popoverVisible.value = false
    drawerVisible.value = false
    emit('copy', currentCopyText.value)
    emit('share')
  } catch (err) {
    BaseMessage.error('复制失败，请手动复制')
  }
}

const handlePopoverVisibleChange = (visible) => {
  if (props.forceDesktopVisible) {
    popoverVisible.value = true
    return
  }
  popoverVisible.value = visible
}

/**
 * @description 保存二维码到本地
 */
const handleSaveQrcode = async () => {
  if (!import.meta.client || !qrDataUrl.value) return

  try {
    const link = document.createElement('a')
    link.download = '分享二维码.png'
    link.href = qrDataUrl.value
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    BaseMessage.success('二维码已保存')
    drawerVisible.value = false
    emit('save')
    emit('share')
  } catch (error) {
    console.error('保存二维码失败:', error)
    BaseMessage.error('保存二维码失败，请稍后重试')
  }
}

defineExpose({
  handleCopyLink,
  handleSaveQrcode,
  close: () => {
    popoverVisible.value = false
    drawerVisible.value = false
  },
})
</script>

<style lang="scss" scoped>
.share-popup {
  display: inline-block;
}

.share-popup__trigger {
  cursor: pointer;
  display: inline-block;
}

.share-popup__default-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: $text-content-color;
  cursor: pointer;
  transition: color 0.3s;

  .icon {
    font-size: 18px;
  }

  &:hover {
    color: $primary-color;
  }
}

.share-popup__popover-container {
  .share-popup__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 180px;

    .share-popup__title {
      font-weight: 600;
      margin-bottom: 12px;
      text-align: center;
      color: $text-title-color;
      font-size: 16px;
      line-height: 24px;
    }

    .share-popup__qrcode {
      display: flex;
      justify-content: center;
      align-items: center;
      margin-bottom: 12px;
      width: 180px;
      height: 180px;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    .share-popup__footer {
      width: 100%;
      display: flex;
      justify-content: center;

      .share-popup__copy-btn {
        color: $primary-color;
        font-size: 14px;
        line-height: 22px;
        height: 30px;
        min-height: 30px;
        padding: 4px 8px;
        border-radius: $radius-6;

        &:hover {
          background-color: $bg-muted-color;
          color: rgba($primary-color, 0.8);
        }

        :deep(.arco-icon) {
          display: block;
          font-size: 16px;
        }

        :deep(.arco-btn-content),
        :deep(.arco-btn-icon) {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          height: 22px;
          line-height: 22px;
        }
      }
    }
  }
}

.share-popup__drawer-body {
  position: relative;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100%;
}

.share-popup__drawer-close {
  position: absolute;
  top: 10px;
  right: 16px;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;

  img {
    width: 20px;
    height: 20px;
  }
}

.share-popup__drawer-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 20px 36px;
}

.share-popup__drawer-title {
  font-size: 24px;
  font-weight: 500;
  color: $text-title-color;
  margin-bottom: 24px;
  text-align: center;
}

.share-popup__drawer-qrcode {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 32px;
  width: 230px;
  height: 230px;
  background-color: $bg-white;
  border-radius: 8px;
  padding: 16px;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.share-popup__drawer-actions {
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 12px;
  width: 100%;
}

.share-popup__drawer-action {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 24px;
  font-size: 14px;
  color: $text-title-color;
  cursor: pointer;
  transition: all 0.3s ease;

  &:active {
    background-color: rgba($text-title-color, 0.05);
  }

  img {
    width: 40px;
    height: 40px;
  }

  .share-popup__drawer-action-icon {
    width: 40px;
    height: 40px;
  }
}
</style>
